import { NgxSpinnerService } from 'ngx-spinner';
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  NgZone,
} from '@angular/core';
import { User } from './shared/models/user.models';
import { NavbarService } from './core/components/navbar/navbar.service';
import { ThemeService } from './shared/service/theme.service';
import {
  Actions,
  ofActionDispatched,
  Select,
  Store,
} from '@ngxs/store';
import { GetUserById, Logout } from './store/auth/auth.actions';
import { Router } from '@angular/router';
import { AuthState } from './store/auth/auth.state';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthStateModel } from './store/auth/auth.model';
import { TranslateService } from '@ngx-translate/core';
import {
  SiteConfigObj,
  SiteConfigService,
} from './shared/service/site-config-service';
import { featureFlag } from '../environments/environment';
import { finalize, tap } from 'rxjs/operators';
import { ThemeInfo } from './modules/setting/component/theme/shared/theme.models';
import { setTheme } from 'ngx-bootstrap/utils';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { AlertService } from './core/services/alert.service';
import { TokenService } from './core/authentication/token.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  currentUser: User;
  navbarActive = false;
  defaultLang = 'en';
  loadingSuccess$ = new BehaviorSubject<boolean>(false);
  countdown?: number = null;
  sessionTimeOut = null;

  @ViewChild('sessionTimoutModal', { static: true })
  sessionTimoutModal: ElementRef;
  modal: NgbModalRef;

  @Select(AuthState.token) userList$: Observable<AuthStateModel>;

  constructor(
    private router: Router,
    private spinnerService: NgxSpinnerService,
    private navbar: NavbarService,
    private themeService: ThemeService,
    private siteConfigService: SiteConfigService,
    private actions: Actions,
    private store: Store,
    private translateService: TranslateService,
    private idle: Idle,
    private keepalive: Keepalive,
    private alertService: AlertService,
    private tokenService: TokenService,
    private modalService: NgbModal,
    private titleService: Title,
    private zone: NgZone,
  ) {
    setTheme('bs4');
    this.actions.pipe(ofActionDispatched(Logout)).subscribe(() => {
      this.router.navigate(['/login']);
    });
    this.initializeLanguage();

    // make sure that site setting is loaded before showing website.
    this.getSiteConfig().then(() => {
      this.getTheme().subscribe(() => {
        this.navbar.data.subscribe((res) => {
          this.navbarActive = res;
        });
      });
    });
  }

  reset(): void {
    // we'll call this method when we want to start/reset the idle process
    // reset any component state and be sure to call idle.watch()
    this.idle.watch();
    this.countdown = null;
  }

  ngOnInit(): void {
    if (
      !this.store.selectSnapshot<string>(
        (state) => state.auth.access,
      ) &&
      this.navbarActive
    ) {
      this.store.dispatch(GetUserById);
    }
    // right when the component initializes, start reset state and start watching
    this.setIdle().then(() => {
      this.reset();
      this.refreshToken();
    });
  }

  getSessionPolicy(): Promise<void> {
    return new Promise((resolve) => {
      this.siteConfigService
        .getSessionPolicy()
        .subscribe((config: any) => {
          this.sessionTimeOut = config?.session_timeout;
          resolve();
        });
    });
  }

  async setIdle() {
    await this.getSessionPolicy();

    if (!this.sessionTimeOut && featureFlag.ndid) {
      this.sessionTimeOut = 60;
    }

    this.idle.setIdle(60 * (this.sessionTimeOut || 60));
    this.idle.setTimeout(15);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.onIdleStart.subscribe(() => {
      if (!this.sessionTimeOut) {
        this.reset();
        return;
      }
      if (this.router.url.includes('login')) {
        this.reset();
        return;
      }

      this.modal = this.modalService.open(this.sessionTimoutModal, {
        centered: true,
        backdrop: 'static',
        keyboard: false,
      });
    });
    this.idle.onTimeout.subscribe(() => {
      this.logout();
    });
    this.idle.onTimeoutWarning.subscribe((seconds) => {
      this.countdown = seconds;
    });
    this.keepalive.interval(4 * 60);
    this.keepalive.onPing.subscribe(() => this.refreshToken());
  }

  /**
   * Set the initial language in localStorage
   * that will be used to stuff in HTTP Headers - `Accept-Language`
   * and for translation.
   */
  initializeLanguage(): void {
    this.defaultLang = localStorage.getItem('lang');
    if (!this.defaultLang || this.defaultLang === 'null') {
      localStorage.setItem('lang', 'en');
      this.translateService.setDefaultLang('en');
    }
    this.defaultLang = localStorage.getItem('lang');
    this.translateService.use(this.defaultLang);
  }

  getTheme(): Observable<ThemeInfo> {
    this.loadingSuccess$.next(false);
    this.spinnerService.show();
    return this.themeService.getTheme().pipe(
      tap((theme) => {
        this.themeService.setThemeList(theme);
      }),
      finalize(() => {
        this.loadingSuccess$.next(true);
        this.spinnerService.hide();
      }),
    );
  }

  getSiteConfig(): Promise<void> {
    return new Promise((resolve) => {
      this.siteConfigService
        .getConfig()
        .subscribe((config: SiteConfigObj) => {
          this.titleService.setTitle(config.page_title);
          featureFlag.ddoc = config.ddoc_enable;
          featureFlag.ddoc_enable_feature_26 =
            config.ddoc_enable_feature_26;
          featureFlag.ddoc_enable_feature_28 =
            config.ddoc_enable_feature_28;
          featureFlag.timestamp_memo_enable =
            config.timestamp_memo_enable;
          featureFlag.login_adfs_enable = config.login_adfs_enable;
          featureFlag.maintenance_mode = config.maintenance_mode;
          featureFlag.maintenance_message_th =
            config.maintenance_message_th;
          featureFlag.maintenance_message_en =
            config.maintenance_message_en;
          featureFlag.ndid = config.ndid_enable;
          featureFlag.sms_otp = config.sms_otp_enable;
          featureFlag.email_otp = config.email_otp_enable;
          featureFlag.thaicom = config.thaicom_enable;
          featureFlag.self_service_form =
            config.self_service_form_enable;
          featureFlag.google_client_id = config.google_client_id;
          featureFlag.export_memo_google_drive_enable =
            config.export_memo_google_drive_enable;

          if (
            featureFlag.maintenance_mode === true &&
            !this.router.url.startsWith('/website-maintenance')
          ) {
            // force redirect to maintenance page when website is in maintenance mode.
            this.zone.run(() => {
              this.router
                .navigate(['/website-maintenance'])
                .then(() => {
                  resolve();
                });
            });
          } else if (
            featureFlag.maintenance_mode !== true &&
            this.router.url.startsWith('/website-maintenance')
          ) {
            // force redirect to login mode when website is not in maintenance.
            this.zone.run(() => {
              this.router.navigate(['/login']).then(() => {
                resolve();
              });
            });
          } else {
            resolve();
          }
        });
    });
  }

  refreshToken(): void {
    if (this.router.url.includes('login')) {
      return;
    }

    const token = this.tokenService.getRefreshToken();
    if (token) {
      this.tokenService.refreshToken(token).subscribe((tk: any) => {
        this.tokenService.saveToken(tk.access);
      });
    }
  }

  logout(): void {
    this.dismissAll();
    this.reset();
    this.store.dispatch(Logout);
  }

  dismissAll(): void {
    this.modalService.dismissAll();
  }

  get checkUrlTypeLoading(): string {
    const isPathRequestForm = this.router.url.includes(
      'customer-register/request-form',
    );
    return isPathRequestForm
      ? 'ball-elastic-dots'
      : 'square-jelly-box';
  }

  get checkUrlBdColorLoading(): string {
    const isPathRequestForm = this.router.url.includes(
      'customer-register/request-form',
    );
    return isPathRequestForm
      ? 'rgba(0, 0, 0, 0.8)'
      : 'rgb(0 0 0 / 59%)';
  }

  get checkUrlSizeLoading():
    | 'default'
    | 'small'
    | 'medium'
    | 'large' {
    const isPathRequestForm = this.router.url.includes(
      'customer-register/request-form',
    );
    return isPathRequestForm ? 'medium' : 'large';
  }
}
